import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, RefreshCw, MousePointerClick, LogOut, Play,
  Search, Timer, Eye, ExternalLink,
} from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const TABS = [
  { key: "clicks", label: "Top klikken", icon: MousePointerClick },
  { key: "dropoff", label: "Drop-off & tijd op pagina", icon: LogOut },
  { key: "sessions", label: "Sessie-verkenner", icon: Play },
];

const RANGES = [
  { key: 1, label: "Vandaag" },
  { key: 7, label: "7 dagen" },
  { key: 30, label: "30 dagen" },
  { key: 90, label: "90 dagen" },
];

const fmt = (v) => (v == null ? "—" : typeof v === "number" ? v.toLocaleString("nl-NL") : v);

// ---------- Tab: Top klikken ----------
const ClickDetail = ({ label, screen, days }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setData(null); setErr("");
    const qs = new URLSearchParams({ days: String(days), label, limit: "100" });
    if (screen) qs.set("screen", screen);
    api(`/admin/analytics/clicks-detail?${qs.toString()}`).then(setData).catch((e) => setErr(e.message));
  }, [label, screen, days]);

  if (err) return <div className="rounded-lg bg-danger/10 p-3 text-xs text-danger">{err}</div>;
  if (!data) return <div className="flex items-center gap-2 py-3 text-xs text-slate-500"><Loader2 className="animate-spin" size={12} /> Details laden…</div>;

  return (
    <div className="rounded-xl bg-slate-50 p-3">
      <div className="mb-2 flex flex-wrap gap-4 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <span data-testid="click-detail-unique-users">{data.unique_users} ingelogde gebruikers</span>
        <span data-testid="click-detail-unique-anon">{data.unique_anon} anoniem</span>
        <span>{data.total_returned} kliks getoond</span>
      </div>
      <div className="max-h-72 space-y-1 overflow-y-auto">
        {data.clicks.length === 0 ? (
          <p className="text-xs text-slate-400">Geen kliks in deze periode.</p>
        ) : data.clicks.map((c, i) => (
          <div key={i} className="flex items-center justify-between gap-3 rounded bg-white px-3 py-1.5 text-xs" data-testid={`click-detail-row-${i}`}>
            <div className="flex min-w-0 items-center gap-2">
              {c.user_email ? (
                <span className="truncate font-semibold text-navy" title={c.user_email}>{c.user_email}</span>
              ) : (
                <span className="text-slate-400">Anoniem <span className="font-mono text-[10px] text-slate-300">{(c.ip_hash || "").slice(0, 8)}</span></span>
              )}
              {c.user_tier === "premium" && <span className="rounded bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-700">Premium</span>}
              {c.user_role === "admin" && <span className="rounded bg-navy/10 px-1.5 text-[10px] font-semibold text-navy">Admin</span>}
              <span className="truncate text-slate-400">op {c.screen}</span>
            </div>
            <span className="shrink-0 text-slate-400">{_fmtTime(c.ts)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ClicksTab = ({ days }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [screen, setScreen] = useState("");
  const [expanded, setExpanded] = useState(null); // label currently expanded

  useEffect(() => {
    setData(null); setErr("");
    setExpanded(null);
    const qs = new URLSearchParams({ days: String(days) });
    if (screen) qs.set("screen", screen);
    api(`/admin/analytics/top-clicks?${qs.toString()}`).then(setData).catch((e) => setErr(e.message));
  }, [days, screen]);

  if (err) return <div className="rounded-xl bg-danger/10 p-4 text-sm text-danger">{err}</div>;
  if (!data) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Laden…</div>;

  const max = Math.max(1, ...(data.top_overall || []).map((x) => x.count));
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-white p-4 shadow-sm">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-400">Totaal klikken</div>
          <div className="font-display text-2xl font-extrabold text-navy" data-testid="clicks-total">{fmt(data.total_clicks)}</div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-500">Filter pagina:</label>
          <select
            className="rounded-lg border border-border bg-white px-3 py-1.5 text-sm"
            data-testid="clicks-screen-filter"
            value={screen}
            onChange={(e) => setScreen(e.target.value)}
          >
            <option value="">Alle pagina&apos;s</option>
            {(data.screen_choices || []).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Top overall */}
      <div className="mt-6 rounded-3xl border border-border bg-white p-6 shadow-sm">
        <h3 className="font-display text-lg font-extrabold text-navy">Top-geklikte elementen</h3>
        <p className="text-xs text-slate-500">Klik op een rij om te zien wie erop klikte.</p>
        {(data.top_overall || []).length === 0 ? (
          <p className="mt-6 text-sm text-slate-400">Nog geen kliks in deze periode.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {data.top_overall.map((row) => {
              const isOpen = expanded === row.label;
              return (
                <div key={row.label} data-testid={`click-row-${row.label}`}>
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : row.label)}
                    className="w-full text-left"
                    data-testid={`click-row-toggle-${row.label}`}
                  >
                    <div className="flex items-center justify-between text-sm">
                      <span className="truncate font-medium text-navy" title={row.label}>
                        <span className="mr-1 text-slate-400">{isOpen ? "▾" : "▸"}</span>{row.label}
                      </span>
                      <span className="ml-3 shrink-0 text-slate-400">{row.count}</span>
                    </div>
                    <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-brandblue" style={{ width: `${(row.count / max) * 100}%` }} />
                    </div>
                  </button>
                  {isOpen && (
                    <div className="mt-2">
                      <ClickDetail label={row.label} screen={screen || null} days={days} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Per screen */}
      {(data.per_screen || []).length > 0 && !screen && (
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          {data.per_screen.map((s) => (
            <div key={s.screen} className="rounded-3xl border border-border bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-navy">{s.screen}</h4>
                <span className="text-xs text-slate-400">{s.total} kliks</span>
              </div>
              <div className="mt-3 space-y-1.5">
                {s.top.map((it) => (
                  <div key={it.label} className="flex items-center justify-between text-xs">
                    <span className="truncate text-slate-600" title={it.label}>{it.label}</span>
                    <span className="ml-2 shrink-0 font-semibold text-slate-500">{it.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Tab: Drop-off ----------
const DropOffTab = ({ days }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setData(null); setErr("");
    api(`/admin/analytics/drop-off?days=${days}`).then(setData).catch((e) => setErr(e.message));
  }, [days]);

  if (err) return <div className="rounded-xl bg-danger/10 p-4 text-sm text-danger">{err}</div>;
  if (!data) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Laden…</div>;

  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-white p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wide text-slate-400">Totaal pageviews</div>
          <div className="font-display text-2xl font-extrabold text-navy" data-testid="dropoff-pv">{fmt(data.total_pageviews)}</div>
        </div>
        <div className="rounded-2xl border border-border bg-white p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wide text-slate-400">Totaal exits</div>
          <div className="font-display text-2xl font-extrabold text-navy" data-testid="dropoff-exits">{fmt(data.total_exits)}</div>
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-3xl border border-border bg-white shadow-sm">
        <table className="w-full text-sm" data-testid="dropoff-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Pagina</th>
              <th className="px-4 py-3 text-right">Pageviews</th>
              <th className="px-4 py-3 text-right">Exits</th>
              <th className="px-4 py-3 text-right">Exit-rate</th>
              <th className="px-4 py-3 text-right">Gem. tijd op pagina</th>
            </tr>
          </thead>
          <tbody>
            {(data.rows || []).length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">Geen data.</td></tr>
            ) : data.rows.map((r) => (
              <tr key={r.screen} className="border-t border-border/60" data-testid={`dropoff-row-${r.screen}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-navy">{r.screen}</span>
                    <a href={r.screen} target="_blank" rel="noreferrer" className="text-slate-300 hover:text-brandblue" aria-label="Open pagina">
                      <ExternalLink size={12} />
                    </a>
                  </div>
                </td>
                <td className="px-4 py-3 text-right text-slate-600">{r.pageviews.toLocaleString("nl-NL")}</td>
                <td className="px-4 py-3 text-right text-slate-600">{r.exits.toLocaleString("nl-NL")}</td>
                <td className="px-4 py-3 text-right">
                  <span className={`font-semibold ${r.exit_rate_pct >= 70 ? "text-danger" : r.exit_rate_pct >= 40 ? "text-amber-600" : "text-success"}`}>
                    {r.exit_rate_pct}%
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-slate-600">
                  {r.avg_dwell_seconds == null ? "—" : `${r.avg_dwell_seconds}s`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Tab: Sessions ----------
const EVENT_ICON = { screen_view: Eye, click: MousePointerClick, page_exit: LogOut, action: Play };
const EVENT_TINT = { screen_view: "text-brandblue", click: "text-amber-600", page_exit: "text-danger", action: "text-slate-500" };

const _fmtDuration = (ms) => {
  if (!ms || ms < 0) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return `${m}m ${rs}s`;
};

const _fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString("nl-NL"); } catch { return iso || ""; }
};

const _clickLabel = (props) => {
  const p = props || {};
  return (p.testid || p.label || p.href || p.tag || "element").toString().slice(0, 80);
};

const SessionsTab = ({ days }) => {
  const [rows, setRows] = useState(null);
  const [email, setEmail] = useState("");
  const [sid, setSid] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState("");
  const debounceRef = useRef(0);

  const load = () => {
    setRows(null); setErr("");
    const qs = new URLSearchParams({ days: String(days) });
    if (email.trim()) qs.set("email", email.trim());
    if (sid.trim()) qs.set("session_id", sid.trim());
    api(`/admin/analytics/sessions?${qs.toString()}`).then((d) => setRows(d.sessions || [])).catch((e) => setErr(e.message));
  };

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, 250);
    return () => clearTimeout(debounceRef.current);
  }, [days, email, sid]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (s) => {
    setSelected(s.session_id);
    setDetail(null);
    api(`/admin/analytics/session/${s.session_id}`).then(setDetail).catch(() => setDetail({ error: true }));
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
      <div className="rounded-3xl border border-border bg-white p-5 shadow-sm">
        <h3 className="font-display text-lg font-extrabold text-navy">Zoek sessies</h3>
        <div className="mt-3 space-y-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              className="rounded-xl pl-9"
              placeholder="E-mailadres (bevat…)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="sess-email"
            />
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              className="rounded-xl pl-9 font-mono text-xs"
              placeholder="Sessie-ID (bevat…)"
              value={sid}
              onChange={(e) => setSid(e.target.value)}
              data-testid="sess-id"
            />
          </div>
        </div>
        {err && <div className="mt-3 text-xs text-danger">{err}</div>}
        <div className="mt-4 max-h-[540px] space-y-1 overflow-y-auto" data-testid="sess-list">
          {!rows ? (
            <div className="flex items-center gap-2 py-4 text-sm text-slate-500"><Loader2 className="animate-spin" size={14} /> Laden…</div>
          ) : rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">Geen sessies gevonden.</p>
          ) : rows.map((s) => (
            <button
              key={s.session_id}
              type="button"
              onClick={() => open(s)}
              className={`block w-full rounded-xl border p-3 text-left transition ${selected === s.session_id ? "border-brandblue bg-brandblue-light/40" : "border-transparent hover:bg-slate-50"}`}
              data-testid={`sess-item-${s.session_id.slice(0, 8)}`}
            >
              <div className="flex items-center justify-between">
                <span className="truncate text-sm font-semibold text-navy">{s.email || "Anoniem"}</span>
                <span className="ml-2 shrink-0 text-xs text-slate-400">{s.event_count} events</span>
              </div>
              <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-500">
                <span className="font-mono">{s.session_id.slice(0, 8)}…</span>
                <span>·</span>
                <span>{s.device}/{s.browser}</span>
              </div>
              <div className="text-[11px] text-slate-400">{_fmtTime(s.last_ts)}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-3xl border border-border bg-white p-6 shadow-sm">
        {!selected ? (
          <div className="flex flex-col items-center justify-center py-16 text-center text-sm text-slate-400">
            <Timer size={32} className="mb-3 text-slate-300" />
            Selecteer een sessie om de tijdlijn te zien.
          </div>
        ) : !detail ? (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Sessie laden…</div>
        ) : detail.error ? (
          <div className="text-sm text-danger">Kon deze sessie niet laden.</div>
        ) : (
          <SessionTimeline detail={detail} />
        )}
      </div>
    </div>
  );
};

const SessionTimeline = ({ detail }) => {
  const evts = detail.events || [];
  const first = evts[0]?.ts;
  const rows = useMemo(() => {
    return evts.map((e, i) => {
      const next = evts[i + 1];
      let dwell = null;
      if (next && e.ts && next.ts) {
        try { dwell = Math.max(0, new Date(next.ts) - new Date(e.ts)); } catch { dwell = null; }
      }
      // Prefer the client-reported dwell_ms on page_exit events.
      if (e.event_type === "page_exit" && e.props && typeof e.props.dwell_ms === "number") {
        dwell = e.props.dwell_ms;
      }
      let sinceStart = null;
      if (first && e.ts) {
        try { sinceStart = new Date(e.ts) - new Date(first); } catch { sinceStart = null; }
      }
      return { e, dwell, sinceStart };
    });
  }, [evts, first]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <div className="font-display text-xl font-extrabold text-navy" data-testid="sess-user-email">
            {detail.user?.email || "Anoniem"}
          </div>
          <div className="text-xs text-slate-500">
            {detail.session.device} · {detail.session.os} · {detail.session.browser}
            {"  ·  "}
            Duur: {_fmtDuration(detail.duration_ms)}
            {"  ·  "}
            {evts.length} events
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-1">
        {rows.map(({ e, dwell, sinceStart }, i) => {
          const Icon = EVENT_ICON[e.event_type] || Play;
          const tint = EVENT_TINT[e.event_type] || "text-slate-500";
          let label = "";
          if (e.event_type === "screen_view") label = e.screen || "/";
          else if (e.event_type === "click") label = _clickLabel(e.props);
          else if (e.event_type === "page_exit") label = `Verliet ${e.screen || "?"}`;
          else label = e.event_name;
          return (
            <div key={i} className="flex items-start gap-3 border-t border-border/60 py-2" data-testid={`ev-${i}`}>
              <span className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-50 ${tint}`}>
                <Icon size={14} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-semibold text-navy">{label}</span>
                  {e.event_type === "click" && e.screen && (
                    <span className="text-[11px] text-slate-400">op {e.screen}</span>
                  )}
                </div>
                <div className="text-[11px] text-slate-400">
                  {_fmtTime(e.ts)}
                  {sinceStart != null && sinceStart > 0 && <> · +{_fmtDuration(sinceStart)}</>}
                  {dwell != null && dwell > 0 && <> · dan {_fmtDuration(dwell)}</>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------- Page ----------
const AdminBehavior = () => {
  useSeo({ title: "Gedrag · Admin", description: "Klik-, exit- en sessie-analyse" });
  const [tab, setTab] = useState("clicks");
  const [days, setDays] = useState(30);
  const [nonce, setNonce] = useState(0);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/admin" className="inline-flex items-center gap-1 hover:text-navy" data-testid="behavior-back"><ArrowLeft size={14} /> Admin</Link>
          </div>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-navy" data-testid="admin-behavior-title">Gedrag & sessies</h1>
          <p className="text-sm text-slate-500">Wat klikken bezoekers aan, waar haken ze af, en wat doen ze binnen één sessie.</p>
        </div>
        <div className="flex items-center gap-2" data-testid="behavior-range">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              data-testid={`bhv-range-${r.key}`}
              onClick={() => setDays(r.key)}
              className={`rounded-full px-3 py-1.5 text-sm font-semibold transition ${days === r.key ? "bg-navy text-amber-300" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            >
              {r.label}
            </button>
          ))}
          <Button variant="ghost" size="sm" onClick={() => setNonce((n) => n + 1)} data-testid="bhv-refresh">
            <RefreshCw size={14} className="mr-1" /> Ververs
          </Button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-1 border-b border-border">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            type="button"
            data-testid={`bhv-tab-${tb.key}`}
            onClick={() => setTab(tb.key)}
            className={`inline-flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-semibold transition ${
              tab === tb.key ? "border-brandblue text-brandblue" : "border-transparent text-slate-500 hover:text-navy"
            }`}
          >
            <tb.icon size={14} /> {tb.label}
          </button>
        ))}
      </div>

      <div className="mt-6" key={`${tab}-${days}-${nonce}`}>
        {tab === "clicks" && <ClicksTab days={days} />}
        {tab === "dropoff" && <DropOffTab days={days} />}
        {tab === "sessions" && <SessionsTab days={days} />}
      </div>
    </div>
  );
};

export default AdminBehavior;
